import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sessionStore } from '@/store/sessionStore';
import { loginWithGoogle, loginWithEmail } from '@/lib/api/endpoints/auth';
import { extractApiError } from '@/lib/api/http';
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton';
import { Button } from '@/design-system/components/Button';
import { Input } from '@/design-system/components/Input';
import { t } from '@/lib/i18n';
import styles from './LoginPage.module.css';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email/password form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? null;

  const handleSuccess = useCallback(
    (result: { user: { role: string }; token: string }) => {
      sessionStore.getState().setSession(result.user as any, result.token);
      const dest = from ?? (result.user.role === 'admin' ? '/admin/rounds' : '/lobby');
      navigate(dest, { replace: true });
    },
    [from, navigate],
  );

  const handleGoogleToken = useCallback(
    async (idToken: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await loginWithGoogle(idToken);
        handleSuccess(result);
      } catch (err) {
        const { code, message } = extractApiError(err);
        if (code === 'EMAIL_NOT_ALLOWED' || code === 'EMPLOYEE_NOT_FOUND' || code === 'NOT_IN_ALLOWLIST') {
          navigate('/denied', { replace: true });
        } else {
          setError(message || t('login.errorGeneric'));
        }
      } finally {
        setLoading(false);
      }
    },
    [handleSuccess, navigate],
  );

  const handleEmailLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password) return;
      setLoading(true);
      setError(null);
      try {
        const result = await loginWithEmail(email.trim(), password);
        handleSuccess(result);
      } catch (err) {
        const { code, message } = extractApiError(err);
        if (code === 'ACCOUNT_DISABLED') {
          navigate('/denied', { replace: true });
        } else if (code === 'INVALID_CREDENTIALS') {
          setError(t('login.errorCredentials'));
        } else {
          setError(message || t('login.errorGeneric'));
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, handleSuccess, navigate],
  );

  return (
    <div className={styles.root}>
      <h1 className={styles.title}>{t('login.title')}</h1>

      {/* Email / password form */}
      <form className={styles.form} onSubmit={handleEmailLogin} noValidate>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">{t('login.emailLabel')}</label>
          <Input
            id="email"
            type="email"
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">{t('login.passwordLabel')}</label>
          <Input
            id="password"
            type="password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !email.trim() || !password}
          loading={loading}
          style={{ width: '100%' }}
        >
          {t('login.submitButton')}
        </Button>
      </form>

      {/* Divider */}
      <div className={styles.divider}>
        <span className={styles.dividerLine} />
        <span className={styles.dividerText}>{t('login.divider')}</span>
        <span className={styles.dividerLine} />
      </div>

      {/* Google button */}
      <div className={styles.googleWrap}>
        <GoogleSignInButton onToken={handleGoogleToken} disabled={loading} />
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {loading && <p className={styles.hint}>{t('login.authenticating')}</p>}
    </div>
  );
}
